import type { ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";

import Hero from "@/components/tools/Hero";
import RelatedTopics from "@/components/tools/RelatedTopics";

export const bookingUrl =
  "https://app.nexhealth.com/appt/smile4me_dental_care?lid=77847&tracking_key=b2d71902-5ce0-43c1-bc95-24cdee27de9a";

export const paragraphClass =
  "text-gray-700 text-base sm:text-lg leading-relaxed";
export const listClass =
  "list-disc list-outside ml-6 space-y-3 text-gray-700 text-base sm:text-lg leading-relaxed";
export const linkClass =
  "inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out";

type RelatedTopic = { label: string; url: string };

export function EditorialServicePage({
  title,
  heroImage,
  heroAlt,
  relatedTopics,
  children,
}: {
  title: string;
  heroImage: string;
  heroAlt: string;
  relatedTopics: RelatedTopic[];
  children: ReactNode;
}) {
  return (
    <>
      <Hero
        imageSrc={heroImage}
        imageAlt={heroAlt}
        imagePosition="center"
        title={title}
        ctaText="Book an Appointment"
        ctaLink={bookingUrl}
      />
      <main className="flex-1 py-8">
        <div className="flex flex-col lg:flex-row mx-auto px-4 sm:px-8 md:px-12">
          <article className="flex-1 space-y-8 pr-0 lg:pr-8">{children}</article>
          <RelatedTopics topics={relatedTopics} />
        </div>
      </main>
    </>
  );
}

export function ServiceSection({ children }: { children: ReactNode }) {
  return (
    <section className="w-full bg-gradient-to-b from-white to-gray-50 py-6 sm:py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 md:px-12 space-y-6 text-center md:text-left">
        {children}
      </div>
    </section>
  );
}

export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 text-center pb-6 after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:mt-2 after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]">
      {children}
    </h2>
  );
}

export function Subheading({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 pt-2">
      {children}
    </h3>
  );
}

export function ContentImage({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="my-8 mx-auto max-w-3xl overflow-hidden rounded-lg shadow-lg">
      <Image
        src={src}
        alt={alt}
        width={1200}
        height={800}
        className="block h-auto w-full"
        sizes="(max-width: 768px) 100vw, 768px"
      />
    </div>
  );
}

export function CTAButtons() {
  return (
    <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
      <Link
        href={bookingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="uppercase font-semibold text-base tracking-wider text-white px-8 py-3 bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] hover:from-[#ECD05B] hover:via-[#AE7D2F] hover:to-[#ECD05B] rounded-lg shadow-lg hover:shadow-inner [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)] hover:scale-105 transition-all duration-300 ease-in-out min-h-[44px] inline-flex items-center justify-center"
      >
        Book an Appointment
      </Link>
      <a
        href="tel:+17184004400"
        className="uppercase font-semibold text-base tracking-wider text-[#AE7D2F] px-8 py-3 bg-white border-2 border-[#AE7D2F] hover:bg-[#AE7D2F] hover:text-white rounded-lg shadow-lg hover:scale-105 transition-all duration-300 ease-in-out min-h-[44px] inline-flex items-center justify-center"
      >
        Call (718) 400-4400
      </a>
    </div>
  );
}

export function ReviewCards({
  reviews,
}: {
  reviews: Array<{ quote: string; author: string }>;
}) {
  return (
    <div className="grid gap-5 md:grid-cols-3">
      {reviews.map((review) => (
        <figure
          key={review.author}
          className="rounded-lg bg-white p-5 text-left shadow-sm"
        >
          <div className="mb-3 text-[#AE7D2F]" aria-label="5 out of 5 stars">
            ⭐️⭐️⭐️⭐️⭐️
          </div>
          <blockquote className={paragraphClass}>{`“${review.quote}”`}</blockquote>
          <figcaption className="mt-4 font-semibold text-gray-800">
            — {review.author}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
